<template>
  <div>
    <!--    title-->
    <v-row
      style="border-bottom: 1px solid #E5E7EB;"
      class="margin-0"
    >
      <v-col
        class="d-flex justify-start block-title"
      >
        <div
          class="block-title--wrapper"
        >
          {{
            item.inspection_date ? formatUtcSqlDateTime(item.inspection_date, dateFormat) : defaultEmptyValue
          }}
        </div>
      </v-col>

      <v-col
        class="d-flex justify-end"
      >
        <active-icon
          :cell-data="item.status"
          class="mr-2"
        />

        <table-row-actions-menu
          :data-index="itemIndex"
          :config="actionsConfig"
          :table-data="data"
        />
      </v-col>
    </v-row>

    <!--    visible data section-->
    <v-row
      v-for="(dataObject, dataObjIndex) in getVisibleItemDetails()"
      :key="dataObjIndex"
      class="margin-0"
    >
      <v-col
        class="data-label"
      >
        {{ dataObject.label }}
      </v-col>

      <v-col
        class="d-flex justify-end data-value"
      >
        {{ dataObject.value }}
      </v-col>
    </v-row>

    <!--    hidden data section-->
    <template
      v-if="slidedDown"
    >
      <v-row
        v-for="(dataObject, dataObjIndex) in getAllItemDetails()"
        :key="'hidden_' + dataObjIndex"
        class="margin-0"
      >
        <v-col
          class="data-label"
        >
          {{ dataObject.label }}
        </v-col>

        <v-col
          class="d-flex justify-end data-value"
        >
          <template
            v-if="dataObject.key === 'images'"
          >
            <table-image-icon-with-counter
              :cell-data="dataObject.value"
              :config="dataObject"
              :row-data="item"
            />
          </template>
          <template
            v-else-if="dataObject.key === 'alarm_on'"
          >
            <cell-icon
              :cell-data="dataObject.value"
              :config="dataObject"
              :row-data="item"
            />
          </template>
          <template
            v-else
          >
            {{ dataObject.value }}
          </template>
        </v-col>
      </v-row>
    </template>

    <!--    arrow up/down button for showing more details-->
    <image-field
      :src="slidedDown ? '/img/icons/arrow-up-base.svg' : '/img/icons/arrow-down-base.svg'"
      class="arrowUpDown"
      @onClick="slidedDown = !slidedDown"
    />

    <information-dialog
      :open-dialog="informationDialogConfig.openDialog"
      :title="informationDialogConfig.title"
      :dialog-message="informationDialogConfig.message"
      :positive-button-text="informationDialogConfig.positiveBtnText"
      :negative-button-text="informationDialogConfig.negativeBtnText"
      :confirm-callback-function="informationDialogConfig.callbackFn"
      @close="$set(informationDialogConfig, 'openDialog', false)"
    />

    <images-preview-dialog
      v-if="imagesPreviewDialogConfig.show"
      :show-dialog="imagesPreviewDialogConfig.show"
      :dialog-title="imagesPreviewDialogConfig.title"
      :images="imagesPreviewDialogConfig.images"
      :field="imagesPreviewDialogConfig.field"
      :remove-callback="imagesPreviewDialogConfig.removeCallback"
      :remove-api-config="imagesPreviewDialogConfig.removeApiConfig"
      @close="imagesPreviewDialogConfig.show = false"
    />
  </div>
</template>

<script>
import ImageField from '@/global/components/fields/ImageField.vue'
import ActiveIcon from '../../components/main-table/ActiveIcon.vue'
import TableRowActionsMenu from '@/global/components/table/components/TableRowActionsMenu.vue'
import InformationDialog from '@/global/components/dialogs/InformationDialog.vue'
import { dateService, FORMATS } from '@/global/services/date'
import TableImageIconWithCounter from '../../components/TableImageIconWithCounter.vue'
import CellIcon from '../../components/main-table/CellIcon.vue'
import previewFiles from '../../helpers/previewFiles'
import ImagesPreviewDialog from '@/global/components/dialogs/ImagesPreviewDialog.vue'
import vehicleInspectionMixin from '../../mixins/vehicleInspectionMixin'
import { showDetailsAction, deleteAction, activationAction } from '@/global/components/table/helpers/tableActions'

const DEFAULT_EMPTY_VALUE = '--'
export default {
  name: 'VehicleInspectionsGridBlock',

  components: {
    ImagesPreviewDialog,
    CellIcon,
    TableImageIconWithCounter,
    InformationDialog,
    TableRowActionsMenu,
    ActiveIcon,
    ImageField
  },

  mixins: [
    previewFiles,
    vehicleInspectionMixin
  ],

  props: {
    data: {
      type: Array,
      default: () => ([])
    },
    item: {
      type: Object,
      required: true,
      default: () => ({})
    },
    itemIndex: {
      type: Number,
      default: 0
    },
    blockConfig: {
      type: Object,
      default: () => ({})
    }
  },

  data () {
    return {
      slidedDown: false,
      informationDialogConfig: {
        openDialog: false,
        message: this.$t('fleet/vehicle.confirm_delete'),
        title: '',
        positiveBtnText: '',
        negativeBtnText: this.$t('base.cancel'),
        callbackFn: () => {}
      },
      documentEmailId: null,
      documentEmailFormData: {},
      documentEmailValidationErrors: {},
      documentValidationErrors: {},
      dateFormat: FORMATS.date,
      imagesPreviewDialogConfig: {
        show: false,
        title: this.$t('fleet/vehicle_comment.images'),
        field: {},
        images: [],
        removeApiConfig: {
          module: 'fleet',
          route: ''
        },
        removeCallback: (event) => this.inspectionRemoveImageCallback(event)
      },
      defaultEmptyValue: DEFAULT_EMPTY_VALUE
    }
  },

  computed: {
    actionsConfig () {
      return {
        tableConfig: {
          actionsConfig: [
            showDetailsAction(this.$t, (event) => this.showInspectionEdit(event)),
            activationAction(this.$t, (event) => this.onInspectionActivation(event), (event) => !!event?.data?.status),
            deleteAction(this.$t, (event) => this.onInspectionDelete(event))
          ]
        }
      }
    }
  },

  methods: {
    formatUtcSqlDateTime (date, format) {
      return dateService.formatFromUtc(date, format)
    },

    getVisibleItemDetails () {
      return [
        {
          label: this.$t('fleet/vehicle_document.valid_until'),
          value: this.item?.valid_until ? dateService.formatFromUtc(this.item.valid_until, FORMATS.date) : DEFAULT_EMPTY_VALUE
        },
        {
          label: this.$t('fleet/vehicle_inspection.inspection_location'),
          value: this.item?.inspection_location || DEFAULT_EMPTY_VALUE
        },
        {
          label: this.$t('fleet/vehicle_inspection.note'),
          value: this.item?.note || DEFAULT_EMPTY_VALUE
        }
      ]
    },

    getAllItemDetails () {
      return [
        {
          key: 'images',
          label: this.$t('fleet/vehicle_document.images'),
          value: this.item?.images || [],
          onClick: (event) => this.showImages(event, {
            module: 'fleet',
            route: 'vehicle-inspection/remove-image/'
          }, 'inspections')
        },
        {
          key: 'alarm_on',
          label: this.$t('fleet/vehicle_document.alarm_on'),
          value: this.item?.alarm_on,
          src: this.item?.alarm_on === 1 ? '/img/icons/green-check-circle.svg' : '/img/icons/grey-check-circle.svg'
        }
      ]
    }
  }
}
</script>

<style scoped lang="scss">
.block-title {
  display: flex;
  justify-content: flex-start;
  font-family: 'Satoshi', sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0;
  color: #111827;
  align-items: center;
  padding-top: 0;
  padding-bottom: 0;
}

.block-title--wrapper {
  display: flex;
  height: 48px;
  align-items: center;
}

.data-label {
  font-family: 'Satoshi', sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0;
  color: #6B7280;
}

.data-value {
  font-family: 'Satoshi', sans-serif;
  font-weight: 500;
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0;
  color: #111827;
  text-align: right;
}

.arrowUpDown {
  position: absolute;
  bottom: 10px;
  left: 50%;
}

.arrowUpDown:hover {
  cursor: pointer;
}
</style>
